import Button from "@ui/button/Button";
import { EntityInput } from "./EntityInput";
import { askConfirm } from "./confirm";
import { ChangeEvent, useEffect, useMemo, useState } from "react";
import { toast } from "react-toastify";
import Config from "src/Config";
import { superAdminFetch as fetch } from "src/services/superAdminFetch";
import { systemErrorMessage, systemPost } from "src/services/superAdminApi";
import { CardholderFlow, type ICardholderFlow } from "./CardholderFlow";
import styled from "styled-components";
import { T } from "../adminTheme";
import { Callout, Check, CountrySelect, DangerZone, Disclosure, Fact, Facts, Fields, Muted, Page, Panel, Row, StatusBadge } from "./adminUi"
import { UserOverviewData } from "./UserOverviewData";

type HolderType = "B2C" | "B2B" | "individual";
type DocumentKind = "front" | "back" | "selfie";

interface ManualKycModel {
  card_holder_uid: string;
  user_uid: string;
  company_uid: string;
  provider_code: string;
  holder_type: HolderType;
  holder_status: string;
  holder_status_message?: string | null;
  provider_holder_id: string | null;
  holder_card_type_id: number | null;
  our_kyc_status: string;
  document: Record<string, string>;
  address: Record<string, string>;
  extras: Record<string, string>;
  kyc_vendor: { provider?: string; reference_id?: string } | null;
  provider_files: Record<string, Record<string, string>>;
  stored_images: Record<string, number>;
  holder: Record<string, string>;
  resolved_for_issuer: Record<string, string>;
  missing_for_issuer: string[];
}

interface ApiError {
  message?: string;
  details?: unknown;
}

interface ApiEnvelope<T> {
  data?: T;
  error?: ApiError;
}

const DOCUMENT_FIELDS = [
  ["first_name", "First name"],
  ["last_name", "Last name"],
  ["middle_name", "Middle name"],
  ["gender", "Gender (M/F)"],
  ["dob", "Date of birth"],
  ["document_type", "Document type"],
  ["document_number", "Document number"],
  ["document_country", "Document country"],
  ["issue_date", "Issue date"],
  ["expiry_date", "Expiry date"],
  ["place_of_birth", "Place of birth"],
  ["issuing_authority", "Issuing authority"],
] as const;

const ADDRESS_FIELDS = [
  ["line1", "Address line 1"],
  ["line2", "Address line 2"],
  ["city", "City name"],
  ["zip", "Postcode"],
  ["country", "Country"],
] as const;

const EXTRA_FIELDS = [
  ["occupation", "Occupation code"],
  ["annual_salary", "Annual salary"],
  ["account_purpose", "Account purpose"],
  ["expected_monthly_volume", "Expected monthly volume"],
  ["ssn", "SSN (US only)"],
] as const;

const HOLDER_FIELDS = [
  ["first_name", "First name"],
  ["last_name", "Last name"],
  ["email", "Email"],
  ["mobile", "Local mobile number"],
  ["area_code", "Area code"],
  ["birthday", "Birthday"],
  ["address_line1", "Address line 1"],
  ["city_code", "Issuer city code"],
  ["postcode", "Postcode"],
  ["country_code", "Country code"],
  ["nationality_code", "Nationality code"],
] as const;

const PROVIDER_CODES = [["wasabi", "Wasabi (sandbox)"], ["wasabiprod", "Wasabi (production)"], ["cardspro", "Cardspro"], ["paytend", "Paytend"]] as const;

const DOCUMENT_FIELD_OPTIONS: Record<string, readonly string[]> = {
  gender: ["M", "F"],
  document_type: ["passport", "id-card", "driver-license", "residence-permit"],
};
const DOCUMENT_DATE_FIELDS = ["dob", "issue_date", "expiry_date"] as const;
const MIN_IMAGE_BYTES = 1024;
const STATUS_POLL_MS = 10000;

const errorText = (error?: ApiError) => {
  if (!error) return "Unknown error";
  const details =
    typeof error.details === "string"
      ? error.details
      : error.details
        ? JSON.stringify(error.details)
        : "";
  return [error.message, details].filter(Boolean).join(": ") || "Unknown error";
};

const isValidIpv4 = (value: string) => {
  const parts = value.split(".");
  return parts.length === 4 && parts.every((part) => {
    if (!/^\d{1,3}$/.test(part)) return false;
    const number = Number(part);
    return number >= 0 && number <= 255 && String(number) === part;
  });
};

const FieldGrid = ({
  fields,
  value,
  onChange,
  disabled = false,
  options,
  dateFields = [],
  countryFields = [],
}: {
  fields: readonly (readonly [string, string])[];
  value: Record<string, string>;
  onChange: (key: string, value: string) => void;
  disabled?: boolean;
  options?: Record<string, readonly string[]>;
  dateFields?: readonly string[];
  countryFields?: readonly string[];
}) => (
  <Fields>
    {fields.map(([key, label]) => (
      <label key={key}>
        {label}
        {countryFields.includes(key) ? (
          <CountrySelect value={value[key] ?? ""} disabled={disabled} onChange={(code) => onChange(key, code)} />
        ) : options?.[key] ? (
          <select
            value={value[key] ?? ""}
            disabled={disabled}
            onChange={(event) => onChange(key, event.target.value)}
          >
            <option value="">Select…</option>
            {options[key].map((option) => (
              <option key={option} value={option}>{option}</option>
            ))}
          </select>
        ) : (
          <input
            type={dateFields.includes(key) ? "date" : "text"}
            disabled={disabled}
            value={value[key] ?? ""}
            onChange={(event) => onChange(key, event.target.value)}
          />
        )}
      </label>
    ))}
  </Fields>
);

/** "address.city_code" → "address › city code" */
const fieldPath = (path: string) => path.split(".").map((part) => part.replaceAll("_", " ")).join(" › ");

const Tile = styled.label<{ $disabled: boolean; $busy: boolean }>`
  display: grid; gap: 2px; min-width: 150px; padding: 12px 16px; border: 1px dashed ${T.faint}; border-radius: ${T.radius};
  background: ${T.surface}; transition: border-color .16s ${T.ease}, background-color .16s ${T.ease}, box-shadow .16s ${T.ease};
  cursor: ${({ $disabled, $busy }) => $busy ? "wait" : $disabled ? "not-allowed" : "pointer"}; opacity: ${({ $disabled }) => $disabled ? 0.6 : 1};
  &:hover { ${({ $disabled }) => $disabled ? "" : `border-color: ${T.accent}; background: ${T.accentSoft};`} }
  &:focus-within { border-color: ${T.accent}; box-shadow: 0 0 0 3px ${T.accentSoft}; }
`;

export const SuperAdminManualKyc = ({
  token,
  initialUserUid = "",
  initialCompanyUid = "",
  initialCardHolderUid = "",
}: {
  token: string;
  initialUserUid?: string;
  initialCompanyUid?: string;
  initialCardHolderUid?: string;
}) => {
  const [holderRef, setHolderRef] = useState({
    card_holder_uid: initialCardHolderUid,
    user_uid: initialUserUid,
    company_uid: initialCompanyUid,
    provider_code: Config.scheme.name === "live" ? "wasabiprod" : "wasabi",
    holder_type: "B2C" as HolderType,
  });
  const [document, setDocument] = useState<Record<string, string>>({
    document_type: "passport",
  });
  const [address, setAddress] = useState<Record<string, string>>({});
  const [extras, setExtras] = useState<Record<string, string>>({});
  const [holder, setHolder] = useState<Record<string, string>>({});
  const [vendor, setVendor] = useState({ provider: "", reference_id: "" });
  const [model, setModel] = useState<ManualKycModel | null>(null);
  const [flow, setFlow] = useState<ICardholderFlow>();
  const [dirty, setDirty] = useState(false);
  const [submitResult, setSubmitResult] = useState<{ status: string; reason: string | null } | null>(null);
  const [checking, setChecking] = useState(false);
  const [polling, setPolling] = useState(false);
  const [statusError, setStatusError] = useState("");
  const [approve, setApprove] = useState(true);
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState<DocumentKind | null>(null);
  const [submitFields, setSubmitFields] = useState({
    bin: "",
    card_type_id: "",
    ip: "",
  });
  const busy = loading || uploading !== null;
  const requiredImages = model?.provider_code === "cardspro"
    ? (document.document_type === "id-card" ? ["front", "back"] : ["front"])
    : [];
  const missingImages = requiredImages.filter(kind => !((model?.stored_images?.[kind] ?? 0) > 0));

  const api = Config.scheme.URL_API;

  const reference = useMemo(() => {
    const cardHolderUid = holderRef.card_holder_uid.trim();
    if (cardHolderUid) return { card_holder_uid: cardHolderUid };
    const result: Record<string, string> = {
      user_uid: holderRef.user_uid.trim(),
      provider_code: holderRef.provider_code.trim(),
      holder_type: holderRef.holder_type,
    };
    if (holderRef.company_uid.trim())
      result.company_uid = holderRef.company_uid.trim();
    return result;
  }, [holderRef]);

  const post = async <T,>(
    method: string,
    body: Record<string, unknown>,
  ): Promise<ApiEnvelope<T>> => {
    const response = await fetch(api + method, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify(body),
    });
    return response.json() as Promise<ApiEnvelope<T>>;
  };

  const applyModel = (next: ManualKycModel) => {
    setModel(next);
    setDirty(false);
    setSubmitFields(previous => ({ ...previous, card_type_id: next.holder_card_type_id ? String(next.holder_card_type_id) : previous.card_type_id }));
    setHolderRef((previous) => ({
      ...previous,
      card_holder_uid: next.card_holder_uid || previous.card_holder_uid,
      user_uid: next.user_uid || previous.user_uid,
      company_uid: next.company_uid || previous.company_uid,
      provider_code: next.provider_code || previous.provider_code,
      holder_type: next.holder_type || previous.holder_type,
    }));
    setDocument(next.document ?? {});
    setAddress(next.address ?? {});
    setExtras(next.extras ?? {});
    const explicitHolderValues = Object.fromEntries(
      Object.entries(next.holder ?? {}).filter(
        ([, value]) => value !== "" && value !== null && value !== undefined,
      ),
    );
    setHolder({ ...(next.resolved_for_issuer ?? {}), ...explicitHolderValues });
    setVendor({
      provider: next.kyc_vendor?.provider ?? "",
      reference_id: next.kyc_vendor?.reference_id ?? "",
    });
  };

  const refreshStatus = async (uid: string) => {
    setChecking(true);
    const response = await systemPost<{ flow?: ICardholderFlow }>(token, "system/cardholders/get", { uid });
    setChecking(false);
    if (response.error) {
      setStatusError(systemErrorMessage(response.error));
      setPolling(false);
      return;
    }
    setStatusError("");
    setFlow(response.data.flow);
    setPolling(["issuer_review", "kyc_under_review"].includes(response.data.flow?.stage ?? ""));
  };

  useEffect(() => {
    if (initialCardHolderUid) void loadManualKyc();
  }, [initialCardHolderUid]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!polling || !model?.card_holder_uid || busy || checking) return;
    const timer = window.setInterval(() => {
      if (globalThis.document.visibilityState !== "hidden") void refreshStatus(model.card_holder_uid);
    }, STATUS_POLL_MS);
    return () => window.clearInterval(timer);
  }, [polling, model?.card_holder_uid, busy, checking, flow]); // eslint-disable-line react-hooks/exhaustive-deps


  const validateReference = () => {
    if (!reference.card_holder_uid && !reference.user_uid) {
      toast.warning("Enter a card holder UID or a user UID");
      return false;
    }
    return true;
  };

  const loadManualKyc = async () => {
    if (!validateReference()) return;
    setLoading(true);
    try {
      const response = await post<ManualKycModel>(
        "system/kyc/manual/get",
        reference,
      );
      if (response.error || !response.data)
        return toast.error("Manual KYC: " + errorText(response.error));
      applyModel(response.data);
      await refreshStatus(response.data.card_holder_uid);
    } catch (error) {
      console.error(error);
      toast.error("Failed to load manual KYC");
    } finally {
      setLoading(false);
    }
  };

  const save = async (reset = false) => {
    if (!validateReference()) return;
    setLoading(true);
    try {
      const normalizedDocument = { ...document };
      if (document.gender)
        normalizedDocument.gender = document.gender.toUpperCase();
      if (document.document_country)
        normalizedDocument.document_country = document.document_country
          .trim()
          .toUpperCase();
      const normalizedAddress = { ...address };
      if (address.country)
        normalizedAddress.country = address.country.trim().toUpperCase();
      const normalizedHolder = { ...holder };
      if (holder.country_code)
        normalizedHolder.country_code = holder.country_code.trim().toUpperCase();
      if (holder.nationality_code)
        normalizedHolder.nationality_code = holder.nationality_code
          .trim()
          .toUpperCase();
      const response = await post<ManualKycModel>("system/kyc/manual/set", {
        ...reference,
        ...(reset
          ? { reset: true }
          : {
              document: normalizedDocument,
              address: normalizedAddress,
              extras,
              holder: normalizedHolder,
              kyc_vendor: vendor.provider ? vendor : undefined,
              approve,
            }),
      });
      if (response.error || !response.data)
        return toast.error("Save failed: " + errorText(response.error));
      applyModel(response.data);
      await refreshStatus(response.data.card_holder_uid);
      toast.success(reset ? "Manual KYC data reset" : "Manual KYC data saved");
    } catch (error) {
      console.error(error);
      toast.error("Failed to save manual KYC");
    } finally {
      setLoading(false);
    }
  };

  const upload = async (
    kind: DocumentKind,
    event: ChangeEvent<HTMLInputElement>,
  ) => {
    const file = event.target.files?.[0];
    event.target.value = "";
    if (!file || !validateReference()) return;
    if (!/^image\/(jpeg|png)$/.test(file.type))
      return toast.warning("Use a JPEG or PNG image");
    if (file.size < MIN_IMAGE_BYTES)
      return toast.warning("The image is smaller than 1 KB and appears truncated");
    setUploading(kind);
    try {
      const content = await new Promise<string>((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => resolve(String(reader.result));
        reader.onerror = () => reject(reader.error);
        reader.readAsDataURL(file);
      });
      const response = await post<ManualKycModel>(
        "system/kyc/manual/document",
        {
          ...reference,
          kind,
          content_base64: content,
          file_name: file.name,
          upload_to_provider: false,
        },
      );
      if (response.error || !response.data)
        return toast.error(
          `${kind} upload failed: ${errorText(response.error)}`,
        );
      applyModel(response.data);
      toast.success(`${kind} image uploaded`);
    } catch (error) {
      console.error(error);
      toast.error(`Failed to upload ${kind} image`);
    } finally {
      setUploading(null);
    }
  };

  const submit = async () => {
    if (!validateReference()) return;
    if (!model || dirty) return toast.warning("Save your changes before submitting");
    if (missingImages.length) return toast.warning(`Upload required images: ${missingImages.join(", ")}`);
    if (model?.missing_for_issuer.length)
      return toast.warning("Complete the missing issuer requirements first");
    const bin = submitFields.bin.trim();
    if (bin && !/^\d{6,8}$/.test(bin))
      return toast.warning("BIN must contain 6 to 8 digits");
    const cardTypeIdText = submitFields.card_type_id.trim();
    const cardTypeId = cardTypeIdText ? Number(cardTypeIdText) : undefined;
    if (!bin && cardTypeId === undefined) return toast.warning("Choose a BIN or card type ID before submitting");
    if (bin && cardTypeId !== undefined) return toast.warning("Choose either a BIN or a card type ID, not both");
    if (
      cardTypeId !== undefined &&
      (!Number.isSafeInteger(cardTypeId) || cardTypeId <= 0)
    )
      return toast.warning("Card type ID must be a positive integer");
    const ip = submitFields.ip.trim();
    if (ip && !isValidIpv4(ip))
      return toast.warning("Buyer IP must be a valid IPv4 address");
    setLoading(true);
    try {
      const body: Record<string, unknown> = { ...reference };
      if (bin) body.bin = bin;
      if (cardTypeId !== undefined) body.card_type_id = cardTypeId;
      if (ip) body.ip = ip;
      const response = await post<{
        status: string;
        provider_holder_id: string | null;
        card_type_id: number | null;
        reason: string | null;
      }>("system/kyc/manual/submit", body);
      if (response.error || !response.data)
        return toast.error("Submit failed: " + errorText(response.error));
      const result = response.data;
      setSubmitResult(result);
      if (["reject", "rejected"].includes(result.status))
        toast.error(result.reason || "Issuer rejected the holder");
      else toast.success(`Issuer status: ${result.status}`);
      await loadManualKyc();
    } catch (error) {
      console.error(error);
      toast.error("Failed to submit holder to issuer");
    } finally {
      setLoading(false);
    }
  };

  const updateBlock =
    (setter: React.Dispatch<React.SetStateAction<Record<string, string>>>) =>
    (key: string, value: string) =>
      { setDirty(true); setter((previous) => ({ ...previous, [key]: value })); };

  const resetLookup = () => {
    setModel(null); setFlow(undefined); setPolling(false); setSubmitResult(null); setDirty(false); setStatusError("");
    setSubmitFields({ bin: "", card_type_id: "", ip: "" });
  };

  return (
    <Page>
      <Panel
        title="Manual issuer KYC"
        hint="Find the card holder, fill in the document and address, upload the images, then submit to the issuer. Use an existing card holder UID or a user UID."
      >
        <Fields>
          {(
            [
              ["card_holder_uid", "Card holder"],
              ["user_uid", "User"],
              ["company_uid", "Company (optional)"],
              ["provider_code", "Issuer"],
            ] as const
          ).map(([key, label]) => (
            <label key={key}>
              {label}
              {key === "provider_code" ? (
                <select
                  value={holderRef[key]}
                  disabled={busy || checking}
                  onChange={(event) => {
                    resetLookup();
                    setHolderRef((previous) => ({ ...previous, [key]: event.target.value }));
                  }}
                >
                  {!PROVIDER_CODES.some(([code]) => code === holderRef[key]) && <option value={holderRef[key]}>{holderRef[key] || "Choose…"}</option>}
                  {PROVIDER_CODES.map(([code, label]) => <option key={code} value={code}>{label}</option>)}
                </select>
              ) : (
                <EntityInput
                  token={token}
                  kind={key === "user_uid" ? "user" : key === "company_uid" ? "company" : "holder"}
                  value={holderRef[key]}
                  disabled={busy || checking}
                  onChange={(value) => {
                    resetLookup();
                    setHolderRef((previous) => ({ ...previous, [key]: value }));
                  }}
                />
              )}
            </label>
          ))}
          <label>
            Holder type
            <select
              value={holderRef.holder_type}
              disabled={busy || checking}
              onChange={(event) => {
                resetLookup();
                setHolderRef((previous) => ({ ...previous, holder_type: event.target.value as HolderType }));
              }}
            >
              <option value="B2C">B2C (private person)</option>
              <option value="B2B">B2B (business)</option>
              <option value="individual">Individual</option>
            </select>
          </label>
        </Fields>
        <Row style={{ marginTop: 16 }}>
          <Button variant="primarySmall" onClick={loadManualKyc} disabled={busy || checking || dirty}>
            {loading ? "Loading…" : "Load / create"}
          </Button>
        </Row>
        {model ? (
          <Facts style={{ marginTop: 20, paddingTop: 16, borderTop: `1px solid ${T.border}` }}>
            <Fact label="Card holder UID"><code>{model.card_holder_uid}</code></Fact>
            <Fact label="Our KYC"><StatusBadge value={model.our_kyc_status} /></Fact>
            <Fact label="Issuer status"><StatusBadge value={model.holder_status} /></Fact>
            <Fact label="Issuer holder ID">{model.provider_holder_id ? <code>{model.provider_holder_id}</code> : "—"}</Fact>
          </Facts>
        ) : null}
      </Panel>

      {model ? (
        <>
          <Panel
            title="Status"
            hint="The latest saved status of this holder at the issuer."
            actions={<Button variant="secondarySmall" disabled={busy || checking} onClick={() => void refreshStatus(model.card_holder_uid)}>{checking ? "Refreshing…" : "Refresh status"}</Button>}
          >
            <div style={{ display: "grid", gap: 12 }}>
              <CardholderFlow flow={flow} />
              {submitResult && <Callout role="status" tone="info">{`Last submission: ${submitResult.status.replaceAll("_", " ")}${submitResult.reason ? ` — ${submitResult.reason}` : ""}`}</Callout>}
              {model.holder_status_message && <Callout tone="warn">{model.holder_status_message}</Callout>}
              {statusError && <Callout role="alert" tone="danger">{`Status refresh failed: ${statusError}`}</Callout>}
              {dirty && <Callout role="status" tone="warn">Unsaved changes — save before uploading images or submitting.</Callout>}
              {polling && <Muted>Waiting for the review result. Saved status refreshes every 10 seconds while this page is visible.</Muted>}
              <Muted style={{ fontSize: 13 }}>This reads the latest saved status. A direct issuer check is not available with a backoffice session.</Muted>
            </div>
          </Panel>
          <Panel title="Document" hint="Identity document details, exactly as printed on the document.">
            <FieldGrid
              fields={DOCUMENT_FIELDS}
              value={document}
              onChange={updateBlock(setDocument)}
              disabled={busy}
              options={DOCUMENT_FIELD_OPTIONS}
              dateFields={DOCUMENT_DATE_FIELDS}
              countryFields={["document_country"]}
            />
          </Panel>
          <Panel title="Address" hint="Residential address of the holder.">
            <FieldGrid
              fields={ADDRESS_FIELDS}
              value={address}
              onChange={updateBlock(setAddress)}
              disabled={busy}
              countryFields={["country"]}
            />
          </Panel>
          <Panel title="Issuer holder data" hint="Seeded from the resolved issuer values, which are the actual payload the issuer will receive.">
            <FieldGrid
              fields={HOLDER_FIELDS}
              value={holder}
              onChange={updateBlock(setHolder)}
              disabled={busy}
            />
          </Panel>
          <Panel title="Additional information" hint="Extra details some issuers ask for, and the vendor that verified the holder.">
            <FieldGrid
              fields={EXTRA_FIELDS}
              value={extras}
              onChange={updateBlock(setExtras)}
              disabled={busy}
            />
            <Fields style={{ marginTop: 14 }}>
              <label>
                KYC vendor
                <select
                  value={vendor.provider}
                  disabled={busy}
                  onChange={(event) => {
                    setDirty(true);
                    setVendor((previous) => ({
                      ...previous,
                      provider: event.target.value,
                    }));
                  }}
                >
                  <option value="">Not provided (leave unchanged)</option>
                  {["SUMSUB", "DIDIT", "MyInfo", "Jumio", "Shufti"].map(
                    (item) => (
                      <option key={item} value={item}>
                        {item}
                      </option>
                    ),
                  )}
                </select>
              </label>
              <label>
                Vendor reference ID
                <input
                  value={vendor.reference_id}
                  disabled={busy}
                  onChange={(event) => {
                    setDirty(true);
                    setVendor((previous) => ({
                      ...previous,
                      reference_id: event.target.value,
                    }));
                  }}
                />
              </label>
            </Fields>
            <div style={{ marginTop: 16 }}>
              <Check
                checked={approve}
                disabled={busy}
                onChange={(event) => { setDirty(true); setApprove(event.target.checked); }}
              >
                Approve our KYC when saving
              </Check>
            </div>
            <Row style={{ marginTop: 16 }}>
              <Button variant="primarySmall" onClick={() => void save()} disabled={busy}>
                Save data
              </Button>
              <Button
                variant="secondarySmall"
                sx={{ color: T.danger }}
                onClick={() => {
                  void askConfirm({
                    title: "Reset manual KYC data?",
                    body: "Removes all accumulated manual KYC fields and uploaded images for this user.",
                    confirmLabel: "Reset",
                    danger: true,
                  }).then((ok) => {
                    if (ok) void save(true);
                  });
                }}
                disabled={busy}
              >
                Reset manual KYC
              </Button>
            </Row>
          </Panel>

          <Panel title="Document images" hint="Images are stored before issuer submission. Cardspro requires the front image and, for an ID card, the back image.">
            {missingImages.length > 0 && <Callout tone="warn" style={{ marginBottom: 14 }}>{`Required images: ${missingImages.join(", ")}`}</Callout>}
            <Row style={{ gap: 12 }}>
              {(["front", "back", "selfie"] as DocumentKind[]).map((kind) => {
                const stored = model.stored_images?.[kind] ?? 0;
                return (
                  <Tile key={kind} $disabled={busy || dirty} $busy={busy}>
                    <input
                      type="file"
                      accept="image/jpeg,image/png"
                      hidden
                      aria-label={`Upload ${kind}`}
                      disabled={busy || dirty}
                      onChange={(event) => void upload(kind, event)}
                    />
                    <span style={{ fontWeight: 600 }}>{uploading === kind ? "Uploading…" : `Upload ${kind}`}</span>
                    <Muted style={{ fontSize: 12.5 }}>
                      {stored > 0 ? `Stored · ${Math.max(1, Math.round(stored / 1024))} KB` : requiredImages.includes(kind) ? "Required" : "Optional"}
                    </Muted>
                  </Tile>
                );
              })}
            </Row>
            {Object.keys(model.provider_files ?? {}).length > 0 && (
              <div style={{ marginTop: 16 }}>
                <Disclosure label="Files known to the issuer">
                  <UserOverviewData value={model.provider_files} />
                </Disclosure>
              </div>
            )}
          </Panel>

          <Panel title="Issuer readiness" hint="Whether the saved data covers everything this issuer asks for.">
            {model.missing_for_issuer.length ? (
              <Callout tone="warn" title="Still missing for the issuer">
                <ul>
                  {model.missing_for_issuer.map((item) => (
                    <li key={item}>{fieldPath(item)}</li>
                  ))}
                </ul>
              </Callout>
            ) : (
              <Callout tone="success">
                Issuer data fields are complete. Card readiness depends on the flow status above.
              </Callout>
            )}
            <div style={{ marginTop: 16 }}><UserOverviewData value={model.resolved_for_issuer ?? {}} /></div>
          </Panel>

          <DangerZone title="Submit this holder to the issuer" hint="Sends the saved data to the issuer and starts its audit.">
            <Muted>A holder is linked to one card type. Enter either a BIN or a card type ID before submitting{model.holder_card_type_id ? ` (current card type: ${model.holder_card_type_id})` : ""}.</Muted>
            <Fields>
              {(
                [
                  ["bin", "BIN"],
                  ["card_type_id", "Card type ID"],
                  ["ip", "Buyer IPv4 (optional)"],
                ] as const
              ).map(([key, label]) => (
                <label key={key}>
                  {label}
                  <input
                    value={submitFields[key]}
                    disabled={busy}
                    type={key === "card_type_id" ? "number" : "text"}
                    min={key === "card_type_id" ? 1 : undefined}
                    step={key === "card_type_id" ? 1 : undefined}
                    inputMode={key === "bin" ? "numeric" : undefined}
                    onChange={(event) =>
                      setSubmitFields((previous) => ({
                        ...previous,
                        [key]: event.target.value,
                      }))
                    }
                  />
                </label>
              ))}
            </Fields>
            <Row>
              <Button
                variant="primarySmall"
                onClick={submit}
                disabled={busy || checking || dirty || model.missing_for_issuer.length > 0 || missingImages.length > 0 || flow?.ready_for_cards === true || ["issuer_review", "kyc_under_review"].includes(flow?.stage ?? "")}
              >
                {loading ? "Submitting…" : "Submit to issuer"}
              </Button>
            </Row>
          </DangerZone>
        </>
      ) : null}
    </Page>
  );
};
